/* eslint-disable @stylistic/padding-line-between-statements */
import { theme } from '@config/theme';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { IconButton } from '@mui/material';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface FileUploadIndicatorProps {
	completed?: boolean;
	label: string;
	progress?: number;
	error?: any;
	removeFile?: () => void;
	path?: string;
}

export const FileUploadIndicator = ({
	label,
	progress,
	error,
	removeFile,
	completed,
	path,
}: FileUploadIndicatorProps) => {
	const { t } = useTranslation();

	const progressDisplay = useMemo(() => {
		if (error || !progress || progress <= 0 || completed) return null;
		return <p className="font-roboto text-base font-normal leading-normal">{progress}%</p>;
	}, [progress, error, completed]);

	const isImage = useMemo(() => {
		return path?.endsWith('.png') || path?.endsWith('.jpg') || path?.endsWith('.jpeg');
	}, [path]);

	const truncatedLabel = useMemo(() => {
		const maxLength = 20;
		const lastDotIndex = label.lastIndexOf('.');

		// If no extension or label is already short enough, handle normally
		if (lastDotIndex === -1 || label.length <= maxLength) {
			return label;
		}

		const filename = label.substring(0, lastDotIndex);
		const extension = label.substring(lastDotIndex);

		// Reserve 3 chars for "..." and at least 7 chars for the filename
		const maxFilenameLength = Math.max(7, maxLength - extension.length - 3);

		return `${filename.substring(0, maxFilenameLength)}...${extension}`;
	}, [label]);

	return (
		<div className="flex flex-col gap-2">
			<div
				className={classNames('w-full border rounded-[8px] border-secondary overflow-hidden', {
					'border-error ': error,
				})}
			>
				<div className="flex justify-between items-center px-[8px] py-[4.5px]">
					{isImage && (
						<img
							src={`${process.env.REACT_APP_CDN_URL}${path}`}
							alt={label}
							className="w-[60px] h-[60px] object-cover rounded-l-[7px] mr-[8px]"
						/>
					)}
					<div className="flex items-center justify-between w-full">
						<p
							className={classNames(
								'font-roboto text-primary text-base font-bold underline decoration-solid',
								{
									'text-error': error,
								}
							)}
						>
							{truncatedLabel}
						</p>
						{progressDisplay}
					</div>
					<IconButton
						disabled={!completed}
						aria-label="delete"
						sx={{
							padding: 0,
						}}
						onClick={removeFile}
					>
						<CancelOutlinedIcon
							sx={{
								color: error ? theme.palette.custom.brightRed : theme.palette.custom.gray,
								fontSize: '24px',
							}}
						/>
					</IconButton>
				</div>

				{!completed && (
					<div className="w-full h-[5px] bg-light rounded-[5px]relative">
						<div className="h-full bg-primary rounded-[5px]" style={{ width: `${progress}%` }} />
					</div>
				)}
			</div>
			{error && (
				<p className="text-error font-roboto text-base font-normal leading-normal">
					{t(error as string)}
				</p>
			)}
		</div>
	);
};
